/**
 * Отладочные логи вложений (upload / WS). Включение только через localStorage.
 *
 * - `debug_attachment_upload=1` — политика upload перед getFullData
 * - `debug_attachment_tape=1` — лента MFileInput (строки, фокус, политика Р7/preview, **жизненный cycle store**)
 *   В store-логах поле `cause`: кто мутировал registry (`selection-reload`, `setValue`, `states-refill`, …).
 * - `debug_r7_prepare=1` — prepare + WS office callback (см. `logR7OfficeCallbackWsDebug`)
 * - `debug_r7_office_ws=1` — только WS `OfficeCallbackHandler.statusChanged`
 * - `debug_ws_js_request=1` — см. `wsJsRequestDebugLog.ts`
 */
import type { FileItem } from '@/common/types/fileItem'
import {
  canOpenAttachmentInR7Editor,
  canOpenOrViewAttachmentInR7Editor,
  canPreviewAttachmentInR7Inline,
  canViewAttachmentInR7Editor,
  getAttachmentUploadSkipReasons,
  isAttachmentR7OpenBlocked,
  isAttachmentRowReadOnly,
  isElementReadOnlyByMeta,
  isElementR7EditBlockedByMeta,
  resolveAttachmentDisplayFileName,
  shouldUploadDsStagedAttachmentOnGetFullData,
  type FileAttachmentsElementMeta,
} from '@/common/utils/attachmentRpcPolicy'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import { isDsAttachmentOpenBlockedPhase } from '@/common/utils/dsAttachmentPhases'
import { findSessionForAttachmentItem } from '@/common/utils/r7AttachmentEditSession'
import {
  ATTACHMENT_STORAGE_URI_FIELD_KEYS,
  getScalarFromFields,
  isAttachmentItemChanged,
  isNewLocalAttachmentItem,
  rowHasServerCode,
} from '@/common/utils/fileAttachmentsUniData'
import { isOfficeFileName } from '@/common/utils/attachmentPreviewUtils'
import { pullAttachmentStoreMutationCause } from '@/common/utils/attachmentStoreMutationCause'

export const ATTACHMENT_UPLOAD_LOG_PREFIX = '[FILEATTACHMENTS getFullData upload]'
export const ATTACHMENT_TAPE_LOG_PREFIX = '[FILEATTACHMENTS tape]'
export const ATTACHMENT_STORE_LOG_PREFIX = '[FILEATTACHMENTS store]'
export const R7_ATTACHMENT_LOG_PREFIX = '[R7 attachment]'

export function isR7AttachmentDebugEnabled(): boolean {
  try {
    return localStorage.getItem('debug_r7_prepare') === '1'
  } catch {
    return false
  }
}

export function isR7OfficeWsDebugEnabled(): boolean {
  try {
    return (
      localStorage.getItem('debug_r7_office_ws') === '1'
      || localStorage.getItem('debug_r7_prepare') === '1'
    )
  } catch {
    return false
  }
}

/** WS `/client/event/office` → `OfficeCallbackHandler.statusChanged`. */
export function logR7OfficeCallbackWsDebug(
  phase: 'ws-inbound' | 'ws-parsed' | 'ws-drop',
  payload: Record<string, unknown>,
): void {
  if (!isR7OfficeWsDebugEnabled()) return
  console.info(R7_ATTACHMENT_LOG_PREFIX, phase, payload)
}

/** DEV: поля prepare / merge для диагностики `getFileDataChunk` на middleware. */
export function logR7AttachmentPrepareDebug(
  phase: 'prepare-request' | 'after-upload-merge' | 'prepare-response',
  payload: Record<string, unknown>,
): void {
  if (!isR7AttachmentDebugEnabled()) return
  console.info(R7_ATTACHMENT_LOG_PREFIX, phase, payload)
}

export function isAttachmentUploadDebugEnabled(): boolean {
  try {
    return localStorage.getItem('debug_attachment_upload') === '1'
  } catch {
    return false
  }
}

export function isAttachmentTapeDebugEnabled(): boolean {
  try {
    return localStorage.getItem('debug_attachment_tape') === '1'
  } catch {
    return false
  }
}

export type AttachmentTapeRowPolicyDebug = {
  canOpenR7?: boolean
  canPreviewInline?: boolean
  syncPhase?: string | null
  previewKind?: string
  propsWired?: {
    showR7EditorButton: boolean
    hasCanOpenR7Editor: boolean
    hasCanPreviewR7Inline: boolean
    hasTapeR7DebugResolver: boolean
  }
  r7?: AttachmentTapeR7PolicyExplanation
}

/** Коды причин отказа open/preview в Р7 (только отладка `debug_attachment_tape`). */
export type AttachmentTapeR7DenialReason =
  | 'element_protection_reading_edit'
  | 'element_is_read_only'
  | 'ds_open_phase'
  | 'session_awaiting_ws'
  | 'missing_file_name'
  | 'not_office_filename'
  | 'no_server_code_not_new_local'
  | 'row_read_only_cannot_edit'
  | 'no_server_code_for_view'

export type AttachmentTapeR7PolicyExplanation = {
  elementMeta: { protection?: string; elementReadOnly: boolean }
  elementId: number | null
  fileName: string
  hasServerCode: boolean
  rowReadOnly: boolean
  isOffice: boolean
  dsPhase: string | null
  canOpen: boolean
  canView: boolean
  canOpenOrView: boolean
  canPreviewInline: boolean
  openDenials: string[]
  previewDenials: string[]
}

const TAPE_R7_DENIAL_LABELS: Record<AttachmentTapeR7DenialReason, string> = {
  element_protection_reading_edit:
    'XML элемента: <protection>=reading — edit в Р7 недоступен (view возможен)',
  element_is_read_only: 'XML элемента: <isReadOnly>=1',
  ds_open_phase: 'фаза DS-снимка блокирует open/preview',
  session_awaiting_ws: 'сессия Р7 в фазе awaiting_ws',
  missing_file_name: 'нет имени файла в строке',
  not_office_filename: 'расширение не Office',
  no_server_code_not_new_local: 'нет code и нет нового локального File',
  row_read_only_cannot_edit: 'строка read-only — edit в Р7 недоступен (view возможен)',
  no_server_code_for_view: 'нет code — view в Р7 недоступен',
}

export function formatAttachmentTapeR7Denial(reason: AttachmentTapeR7DenialReason): string {
  return TAPE_R7_DENIAL_LABELS[reason] ?? reason
}

function normalizeTapeDebugElementId(elementId?: number | null): number | null {
  const eid = elementId != null ? Number(elementId) : NaN
  return Number.isFinite(eid) && eid > 0 ? eid : null
}

function resolveTapeDebugDsPhase(
  item: FileItem,
  elementId?: number | null,
): string | null {
  const eid = normalizeTapeDebugElementId(elementId)
  if (!eid) return null
  try {
    const phase = getDsAttachmentStore(eid).getPhase(item)
    return phase === 'none' ? null : phase
  } catch {
    return null
  }
}

function collectTapeR7SharedDenials(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): AttachmentTapeR7DenialReason[] {
  const denials: AttachmentTapeR7DenialReason[] = []
  if (meta.elementReadOnly) {
    denials.push('element_is_read_only')
  }

  const eid = normalizeTapeDebugElementId(elementId)
  if (eid) {
    try {
      const phase = getDsAttachmentStore(eid).getPhase(item)
      if (isDsAttachmentOpenBlockedPhase(phase)) {
        denials.push('ds_open_phase')
      }
    } catch {
      // ignore invalid elementId
    }
  }

  const session = findSessionForAttachmentItem(item, elementId)
  if (session?.phase === 'awaiting_ws') {
    denials.push('session_awaiting_ws')
  }

  return denials
}

function collectTapeR7OpenDenials(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): AttachmentTapeR7DenialReason[] {
  if (canOpenOrViewAttachmentInR7Editor(item, meta, elementId)) return []

  const shared = collectTapeR7SharedDenials(item, meta, elementId)
  if (shared.length > 0) return shared

  const fileName = resolveAttachmentDisplayFileName(item)
  if (!fileName) return ['missing_file_name']
  if (!isOfficeFileName(fileName)) return ['not_office_filename']

  const fields = (item.fields ?? {}) as Record<string, unknown>
  const hasServerCode = rowHasServerCode(fields)
  const rowReadOnly = isAttachmentRowReadOnly(item)

  if (rowReadOnly) {
    return hasServerCode ? ['row_read_only_cannot_edit'] : ['no_server_code_for_view']
  }

  if (meta.protection === 'reading') {
    if (hasServerCode || isNewLocalAttachmentItem(item)) {
      return ['element_protection_reading_edit']
    }
    return ['no_server_code_not_new_local']
  }

  if (!hasServerCode && !isNewLocalAttachmentItem(item)) {
    return ['no_server_code_not_new_local']
  }

  if (isElementR7EditBlockedByMeta(meta)) {
    return ['element_protection_reading_edit']
  }

  return ['no_server_code_not_new_local']
}

function collectTapeR7PreviewDenials(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): AttachmentTapeR7DenialReason[] {
  if (canPreviewAttachmentInR7Inline(item, meta, elementId)) return []

  if (isAttachmentR7OpenBlocked(item, elementId)) {
    const session = findSessionForAttachmentItem(item, elementId)
    if (session?.phase === 'awaiting_ws') return ['session_awaiting_ws']
    return ['ds_open_phase']
  }

  if (isElementReadOnlyByMeta(meta)) return ['element_is_read_only']

  const fileName = resolveAttachmentDisplayFileName(item)
  if (!fileName) return ['missing_file_name']
  if (!isOfficeFileName(fileName)) return ['not_office_filename']

  const rowReadOnly = isAttachmentRowReadOnly(item)
  if (rowReadOnly || !isNewLocalAttachmentItem(item)) {
    return ['no_server_code_not_new_local']
  }

  return ['no_server_code_not_new_local']
}

/** Разбор политики Р7 для строки ленты (`debug_attachment_tape`). */
export function explainAttachmentR7PolicyForTape(
  item: FileItem,
  meta: FileAttachmentsElementMeta,
  elementId?: number | null,
): AttachmentTapeR7PolicyExplanation {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const fileName = resolveAttachmentDisplayFileName(item)
  const hasServerCode = rowHasServerCode(fields)
  const rowReadOnly = isAttachmentRowReadOnly(item)
  const canOpen = canOpenAttachmentInR7Editor(item, meta, elementId)
  const canView = canViewAttachmentInR7Editor(item, meta, elementId)
  const canOpenOrView = canOpen || canView
  const canPreviewInline = canPreviewAttachmentInR7Inline(item, meta, elementId)

  const openReasons = collectTapeR7OpenDenials(item, meta, elementId)
  const previewReasons = collectTapeR7PreviewDenials(item, meta, elementId)

  return {
    elementMeta: {
      protection: meta.protection,
      elementReadOnly: meta.elementReadOnly,
    },
    elementId: normalizeTapeDebugElementId(elementId),
    fileName,
    hasServerCode,
    rowReadOnly,
    isOffice: Boolean(fileName) && isOfficeFileName(fileName),
    dsPhase: resolveTapeDebugDsPhase(item, elementId),
    canOpen,
    canView,
    canOpenOrView,
    canPreviewInline,
    openDenials: canOpenOrView ? [] : openReasons.map(formatAttachmentTapeR7Denial),
    previewDenials: canPreviewInline ? [] : previewReasons.map(formatAttachmentTapeR7Denial),
  }
}

/** Снимок строки ленты без тяжёлых полей (`preview`, бинарники). */
export function buildAttachmentTapeRowDebugSnapshot(
  item: FileItem,
  index: number,
  policy?: AttachmentTapeRowPolicyDebug,
): Record<string, unknown> {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const name = attachmentRowLabel(item)
  const previewField = fields.preview
  const previewBytes =
    previewField != null
    && typeof previewField === 'object'
    && 'value' in (previewField as object)
      ? String((previewField as { value: unknown }).value ?? '').length
      : typeof previewField === 'string'
        ? previewField.length
        : 0

  return {
    index: index + 1,
    name,
    changed: item.changed ?? false,
    changedEffective: isAttachmentItemChanged(item),
    hasNativeFile: item.file instanceof File,
    nativeFileName: item.file instanceof File ? item.file.name : null,
    hasServerCode: rowHasServerCode(fields),
    code: getScalarFromFields(fields, 'code', '_id') ?? null,
    version: getScalarFromFields(fields, 'version', '_versionId') ?? null,
    uneditable: getScalarFromFields(fields, 'uneditable') ?? null,
    storageUri: getScalarFromFields(fields, ...ATTACHMENT_STORAGE_URI_FIELD_KEYS) ?? null,
    documentSessionId:
      getScalarFromFields(fields, '__documentSessionId', 'documentSessionId') ?? null,
    isOffice: isOfficeFileName(name),
    previewFieldLength: previewBytes,
    policy: policy ?? null,
  }
}

export function logAttachmentTapeDebug(
  phase: 'tape-sync' | 'focus',
  options: {
    tableFieldName?: string
    items: FileItem[]
    focused?: FileItem | null
    focusedIndex?: number
    resolveRowPolicy?: (item: FileItem, index: number) => AttachmentTapeRowPolicyDebug | undefined
  },
): void {
  if (!isAttachmentTapeDebugEnabled()) return

  const { tableFieldName, items, focused, focusedIndex, resolveRowPolicy } = options
  const rows = items.map((item, index) =>
    buildAttachmentTapeRowDebugSnapshot(item, index, resolveRowPolicy?.(item, index)),
  )

  console.info(ATTACHMENT_TAPE_LOG_PREFIX, phase, {
    tableFieldName: tableFieldName ?? null,
    rowsTotal: items.length,
    focusedIndex: focusedIndex ?? null,
    focusedName: focused ? attachmentRowLabel(focused) : null,
    rows,
  })
}

function attachmentRowLabel(item: FileItem): string {
  const fields = (item.fields ?? {}) as Record<string, unknown>
  const nameField = fields.name ?? fields.fileName
  const name =
    nameField != null &&
    typeof nameField === 'object' &&
    'value' in (nameField as object)
      ? String((nameField as { value: unknown }).value)
      : ''
  return name || '(без имени)'
}

/** Краткое описание typed-поля attachments в store / refill payload. */
export function summarizeAttachmentFieldEntry(entry: unknown): {
  entryType: string | null
  rowCount: number | null
  names: string[]
  looksEmpty: boolean
} {
  if (entry == null) {
    return { entryType: null, rowCount: null, names: [], looksEmpty: true }
  }
  const entryType =
    typeof entry === 'object' && entry !== null && 'type' in entry
      ? String((entry as { type?: unknown }).type ?? '')
      : null
  let raw: unknown = entry
  if (typeof entry === 'object' && entry !== null && 'value' in entry) {
    raw = (entry as { value: unknown }).value
  }
  if (raw == null) {
    return { entryType, rowCount: null, names: [], looksEmpty: true }
  }
  if (!raw || typeof raw !== 'object') {
    return { entryType, rowCount: null, names: [], looksEmpty: true }
  }
  const rows = (raw as { rows?: unknown[] }).rows
  if (!Array.isArray(rows)) {
    return { entryType, rowCount: null, names: [], looksEmpty: true }
  }
  const names = rows.map((row) => {
    const fields =
      row && typeof row === 'object' && 'fields' in row
        ? (row as { fields: Record<string, unknown> }).fields
        : (row as Record<string, unknown>)
    const name = getScalarFromFields(fields ?? {}, 'name', 'fileName')
    return name != null ? String(name) : '(без имени)'
  })
  return {
    entryType,
    rowCount: rows.length,
    names,
    looksEmpty: rows.length === 0,
  }
}

export function summarizeAttachmentFileItems(items: FileItem[]): {
  rowCount: number
  names: string[]
  serverCodes: Array<number | null>
} {
  return {
    rowCount: items.length,
    names: items.map((item) => attachmentRowLabel(item)),
    serverCodes: items.map((item) => {
      const fields = (item.fields ?? {}) as Record<string, unknown>
      const code = Number(getScalarFromFields(fields, 'code', '_id'))
      return Number.isFinite(code) ? code : null
    }),
  }
}

/** Store / states / refill — тот же флаг, что и лента: `debug_attachment_tape=1`. */
export function logAttachmentStoreDebug(phase: string, payload: Record<string, unknown>): void {
  if (!isAttachmentTapeDebugEnabled()) return
  let out = payload
  if (
    !('cause' in payload) &&
    payload.fieldName != null &&
    String(payload.fieldName).trim() !== '' &&
    payload.elementId != null
  ) {
    const pulled = pullAttachmentStoreMutationCause(
      String(payload.fieldName),
      payload.elementId as string | number,
    )
    if (pulled) {
      out = {
        cause: pulled.cause,
        ...(pulled.detail ? { causeDetail: pulled.detail } : {}),
        ...payload,
      }
    }
  }
  console.info(ATTACHMENT_STORE_LOG_PREFIX, phase, out)
}

const UPLOAD_SKIP_LABELS: Record<string, string> = {
  element_protection_reading: 'XML <protection>=reading',
  element_is_read_only: 'XML <isReadOnly>=1',
  no_local_file: 'нет локального File (только метаданные в store)',
  has_server_code: 'у строки уже есть code (не новое вложение)',
  changed_false: 'нет changed и нет нового локального File без code',
  row_read_only_uneditable: 'строка read-only: атрибут uneditable',
  row_read_only_signed: 'строка read-only: непустой signature_file_list',
}

export function attachmentUploadDebugWarn(message: string, ...details: unknown[]): void {
  if (!isAttachmentUploadDebugEnabled()) return
  console.warn(ATTACHMENT_UPLOAD_LOG_PREFIX, message, ...details)
}

export function logAttachmentUploadPolicyForGetFullData(options: {
  elementName?: string
  elementMeta: FileAttachmentsElementMeta
  items: FileItem[]
  pendingCount: number
  sessionId?: string | null
  uploadElementId?: number | null
}): void {
  if (!isAttachmentUploadDebugEnabled()) return

  const { elementName, elementMeta, items, pendingCount, sessionId, uploadElementId } = options

  attachmentUploadDebugWarn('проверка политики upload', {
    elementName: elementName ?? null,
    elementMeta,
    rowsTotal: items.length,
    rowsToUpload: pendingCount,
    sessionId: sessionId ?? null,
    uploadElementId: uploadElementId ?? null,
  })

  if (items.length === 0) {
    attachmentUploadDebugWarn('нет строк вложений в компоненте/store')
    return
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const dsStaged = shouldUploadDsStagedAttachmentOnGetFullData(
      item,
      elementMeta,
      uploadElementId,
    )
    const reasons = getAttachmentUploadSkipReasons(item, elementMeta)
    if (dsStaged) {
      const dsDirty =
        uploadElementId != null
        && Number.isFinite(Number(uploadElementId))
        && Number(uploadElementId) > 0
          ? getDsAttachmentStore(Number(uploadElementId)).getSnapshot(item)?.dsDirty === true
          : false
      attachmentUploadDebugWarn(`строка #${i + 1} «${attachmentRowLabel(item)}»`, '→ R7 promote upload', {
        dsDirty,
      })
      continue
    }
    if (reasons.length === 0) {
      attachmentUploadDebugWarn(`строка #${i + 1} «${attachmentRowLabel(item)}»`, '→ будет загружена')
      continue
    }
    attachmentUploadDebugWarn(
      `строка #${i + 1} «${attachmentRowLabel(item)}»`,
      'upload пропущен, не выполнено:',
      reasons.map((r) => UPLOAD_SKIP_LABELS[r] ?? r),
      {
        changedFlag: item.changed ?? false,
        changedEffective: isAttachmentItemChanged(item),
        isNewLocal: isNewLocalAttachmentItem(item),
        hasFile: item.file instanceof File,
        fileName: item.file instanceof File ? item.file.name : null,
      },
    )
  }

  if (pendingCount === 0) {
    attachmentUploadDebugWarn('ни одна строка не прошла все условия — sync upload не вызывается')
  } else if (!sessionId || uploadElementId == null || !elementName) {
    const missing: string[] = []
    if (!sessionId) missing.push('sessionId')
    if (uploadElementId == null) missing.push('elementId')
    if (!elementName) missing.push('elementName')
    attachmentUploadDebugWarn('строки готовы к upload, но нет контекста сессии:', missing.join(', '))
  }
}
